import {
  getStorage,
  readStorageJSON,
  removeStorageItem,
  writeStorageJSON
} from '@/utils/secure-storage';

const MNEMONIC_KEY = 'wallet-mnemonic';
const PASSWORD_KEY = 'wallet-password';
const SIGNER_SECRET_KEY = 'wallet-signer-secret';
const TEMP_IMPORT_SECRET_KEY = 'wallet-import-secret';
const SENSITIVE_EXPORT_KEY = 'wallet-sensitive-export';
const CARD_INFO_ACCESS_KEY = 'card-info-access';

function readRawMap(storage, key) {
  return readStorageJSON(key, {}, { storage });
}

function writeRawMap(storage, key, data) {
  writeStorageJSON(key, data, { storage });
}

function readMap(key) {
  const local = getStorage('local');
  const session = getStorage('session');
  const localData = readRawMap(local, key);
  const sessionData = readRawMap(session, key);

  if (Object.keys(sessionData).length) {
    const merged = {
      ...sessionData,
      ...localData
    };
    writeRawMap(local, key, merged);
    removeStorageItem(key, { storage: session });
    return merged;
  }

  return localData;
}

function writeMap(key, data) {
  const local = getStorage('local');
  writeRawMap(local, key, data);
}

function readMigratedJSON(key, fallback = null) {
  const local = getStorage('local');
  const session = getStorage('session');
  const localData = readStorageJSON(key, null, { storage: local });
  if (localData !== null) {
    return localData;
  }

  const sessionData = readStorageJSON(key, null, { storage: session });
  if (sessionData !== null) {
    writeStorageJSON(key, sessionData, { storage: local });
    removeStorageItem(key, { storage: session });
    return sessionData;
  }

  return fallback;
}

function writeLocalJSON(key, value) {
  writeStorageJSON(key, value, {
    storage: getStorage('local')
  });
}

function removeLocalItem(key) {
  removeStorageItem(key, {
    storage: getStorage('local')
  });
}

function normalizeAddress(address) {
  return String(address || '').toLowerCase();
}

export function saveWalletMnemonic(address, mnemonic) {
  if (!address || !mnemonic) return;
  const data = readMap(MNEMONIC_KEY);
  data[normalizeAddress(address)] = mnemonic;
  writeMap(MNEMONIC_KEY, data);
}

export function getWalletMnemonic(address) {
  if (!address) return '';
  const data = readMap(MNEMONIC_KEY);
  return data[normalizeAddress(address)] || '';
}

export function getRecoverableMnemonic(address) {
  const mnemonic = getWalletMnemonic(address);
  if (mnemonic) {
    return mnemonic;
  }

  const signer = getWalletSignerSecret(address);
  if (signer?.type === 'mnemonic' && signer?.secret) {
    return String(signer.secret);
  }

  return '';
}

export function clearWalletMnemonic(address) {
  if (!address) return;
  const data = readMap(MNEMONIC_KEY);
  delete data[normalizeAddress(address)];
  writeMap(MNEMONIC_KEY, data);
}

export function saveWalletPassword(address, password) {
  if (!address || !password) return;
  const data = readMap(PASSWORD_KEY);
  data[normalizeAddress(address)] = password;
  writeMap(PASSWORD_KEY, data);
}

export function getWalletPassword(address) {
  if (!address) return '';
  const data = readMap(PASSWORD_KEY);
  return data[normalizeAddress(address)] || '';
}

export function saveWalletSignerSecret(address, secret, type = 'mnemonic') {
  if (!address || !secret) return;
  const data = readMap(SIGNER_SECRET_KEY);
  data[normalizeAddress(address)] = {
    secret: String(secret),
    type: String(type || 'mnemonic')
  };
  writeMap(SIGNER_SECRET_KEY, data);
}

export function getWalletSignerSecret(address) {
  if (!address) return null;
  const data = readMap(SIGNER_SECRET_KEY);
  return data[normalizeAddress(address)] || null;
}

export function saveTempImportSecret(payload) {
  if (!payload?.address || !payload?.secret) return;
  writeLocalJSON(TEMP_IMPORT_SECRET_KEY, {
    address: normalizeAddress(payload.address),
    secret: String(payload.secret),
    type: String(payload.type || 'mnemonic')
  });
}

export function getTempImportSecret(address) {
  try {
    const data = readMigratedJSON(TEMP_IMPORT_SECRET_KEY, null);
    if (!data) return null;
    if (data?.address && normalizeAddress(address) === data.address) {
      return data;
    }
    return null;
  } catch {
    return null;
  }
}

export function clearTempImportSecret() {
  removeLocalItem(TEMP_IMPORT_SECRET_KEY);
}

export function saveSensitiveExportPayload(payload) {
  writeLocalJSON(SENSITIVE_EXPORT_KEY, payload || {});
}

export function getSensitiveExportPayload() {
  return readMigratedJSON(SENSITIVE_EXPORT_KEY, {});
}

export function clearSensitiveExportPayload() {
  removeLocalItem(SENSITIVE_EXPORT_KEY);
}

export function saveCardInfoAccess(cardId) {
  const value = String(cardId || '').trim();
  if (!value) return;
  const data = readMigratedJSON(CARD_INFO_ACCESS_KEY, {});
  data[value] = Date.now();
  writeLocalJSON(CARD_INFO_ACCESS_KEY, data);
}

export function consumeCardInfoAccess(cardId) {
  const value = String(cardId || '').trim();
  if (!value) return false;
  const data = readMigratedJSON(CARD_INFO_ACCESS_KEY, {});
  const allowed = Boolean(data[value]);
  if (allowed) {
    delete data[value];
    writeLocalJSON(CARD_INFO_ACCESS_KEY, data);
  }
  return allowed;
}
